import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Tooltip,
  Typography,
  DialogActions,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SelectChainItem, SelectConfig, SelectResult } from '../../../components/select/select-chain.component';
import { useSaveData } from '../../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import { useState } from 'react';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { Service } from '../../../models/masters/service';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { workOrderServicesColumns } from './workorder-services-columns';
import { customersApiKeyStore } from '../../../api/customers-api-key-store';
import { officeApiKeyStore } from '../../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { unitsApiKeyStore } from '../../../api/units-api-key-store';
import { Icons } from '../../../assets/icons';
import { Link } from 'react-router-dom';
import ReportIcon from '@mui/icons-material/Report';
import { UserPermissionName } from '../../../models/user-permission-name';
import ConfirmDialog from '../../../components/confirm-dialog';
import { useAuthorize } from '../../../hooks/use-authorize';

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
  buttonPadding: { paddingTop: '20px' },
  tabHeading: { fontWeight: 'bold' },
  buttonStyle: { width: '130px' },
};

const DEFAULT_GROUP = [
  {
    field: 'serviceGroupName',
  },
];

interface CellProps {
  serviceItem: any;
}

interface CustomGridCellProps {
  field: string;
}

interface CreateWorkorderDialogProps {
  open: boolean;
  onClose: () => void;
  selectedFilters?: SelectResult;
  handleWorkOrderRefresh?: (value: boolean) => void;
  page?: string;
}

export const CreateWorkorderDialog = ({
  open,
  onClose,
  selectedFilters,
  handleWorkOrderRefresh,
  page,
}: CreateWorkorderDialogProps) => {
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [woSelectedFilters, setSelectedFilters] = useState<any>(selectedFilters ?? {});
  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);
  // const { data, isLoading, isFetching } = useLoadData<ApiResponse<Service[]>>(
  //   servicesApiKeyStore.getServiceUnitMappings(woSelectedFilters ? woSelectedFilters.unitID : 0),
  //   !!woSelectedFilters?.unitID
  // );

  const { data, isLoading, isFetching } = useLoadData<ApiResponse<Service[]>>(
    workOrdersApiKeyStore.getServiceMappingListForWorkorder(
      0,
      0,
      woSelectedFilters?.projectID,
      woSelectedFilters?.unitID,
      0
    ),

    !!woSelectedFilters?.unitID && open
  );

  const { isLoading: isSaving, mutateAsync } = useSaveData(workOrdersApiKeyStore.generateWorkOrder());

  const handleDialogClose = () => {
    onClose();
  };

  const handleServiceSelection = (values: Service[]) => {
    setSelectedServices(values);
  };

  const handleSaveClick = async () => {
    if (selectedServices && selectedServices.some((p) => p.workOrderExists)) {
      // const confirmResponse = confirm(
      //   'Work orders already exists for the selected services. Do you still want to create new?'
      // );
      // if (confirmResponse)
      //   await mutateAsync({
      //     data: { unitID: woSelectedFilters?.unitID, services: selectedServices },
      //   });
      // // handleDialogClose();
      // handleWorkOrderRefresh?.(true);
      setIsConfirmation(true);
    } else {
      await mutateAsync({
        data: { unitID: woSelectedFilters?.unitID, services: selectedServices },
      });
      handleDialogClose();
      handleWorkOrderRefresh?.(true);
    }
  };

  const handleConfirm = async () => {
    await mutateAsync({
      data: { unitID: woSelectedFilters?.unitID, services: selectedServices },
    });
    setIsConfirmation(false);
    handleDialogClose();
    handleWorkOrderRefresh?.(true);
  };

  const handleServiceEdit = (value: Service) => {
    const index = selectedServices.findIndex((s) => s.serviceID === value.serviceID);
    if (index >= 0) {
      selectedServices[index] = value;
      setSelectedServices(selectedServices);
    }
  };

  const handleClose = () => {
    setIsConfirmation(false);
  };

  const selectConfig: SelectConfig[] = [
    {
      title: 'Customer',
      optionsLabelKey: 'customerName',
      optionsValueKey: 'customerID',
      type: 'autocomplete',
      disabled: !page ? !!selectedFilters?.customerID : false,
      getQueryKeyConfig: () => customersApiKeyStore.getCustomers(),
    },
    {
      title: 'Office',
      optionsLabelKey: 'officeName',
      optionsValueKey: 'officeID',
      type: 'autocomplete',
      disabled: !page ? !!selectedFilters?.officeID : false,
      getQueryKeyConfig: (value?: SelectResult) => officeApiKeyStore.getCustomerOffices(value?.customerID),
    },
    {
      title: 'Project',
      optionsLabelKey: 'projectName',
      optionsValueKey: 'projectID',
      type: 'autocomplete',
      disabled: !page ? !!selectedFilters?.projectID : false,
      getQueryKeyConfig: (value?: SelectResult) => projectsApiKeyStore.getOfficeProjects(value?.officeID),
    },
    {
      title: 'Units',
      optionsLabelKey: 'unitName',
      optionsValueKey: 'unitID',
      type: 'autocomplete',
      disabled: !page ? !!selectedFilters?.unitID : false,
      getQueryKeyConfig: (value?: SelectResult) => unitsApiKeyStore.getProjectUnits(value?.projectID),
    },
  ];

  const handleFilterSelected = (filter: SelectResult) => {
    setSelectedFilters(filter);
  };

  const ShowWarning = ({ serviceItem }: CellProps) => {
    return (
      <td>
        <Typography variant="body1" style={{ cursor: 'pointer' }}>
          {/* {bundleService?.bundlePrice !== 0 && `$${bundleService?.bundlePrice}`} */}

          {serviceItem?.workOrderExists ? (
            <Tooltip title={<Typography variant="body1">Work order already exists for this service</Typography>}>
              <ReportIcon style={{ color: 'orange' }} />
            </Tooltip>
          ) : (
            ''
          )}
        </Typography>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'warning') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <ShowWarning serviceItem={dataItem.dataItem} />;
      };
    }
    return undefined;
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <Dialog
        open={open}
        fullWidth={true}
        maxWidth={'xl'}
        // onClose={handleDialogClose}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
          Create Workorder
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: 'absolute',
            right: 10,
            top: 3,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Tooltip title={<Typography variant="body1">Close</Typography>}>
            <CloseIcon />
          </Tooltip>
        </IconButton>
        <DialogContent dividers style={styling.contentPadding} className="popupDilog tableLastColumnHide">
          <div className="mBm">
            <SelectChainItem config={selectConfig} value={woSelectedFilters} onSelect={handleFilterSelected} />
          </div>
          <SiTrackGrid
            fileTitle='workorder'
            dataItemKey="serviceID"
            columns={
              selectedServices?.length > 0
                ? workOrderServicesColumns
                : workOrderServicesColumns?.filter((column) => column?.field !== 'warning')
            }
            dataItems={data?.data}
            selectedField="isSelected"
            customCell={(field) =>
              CustomGridCell({
                field,
              })
            }
            onSelectedItems={handleServiceSelection}
            onEditComplete={handleServiceEdit}
            isBusy={isLoading || isFetching}
            // defaultGroup={DEFAULT_GROUP}
            actionPermissions={{
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
          />
        </DialogContent>
        <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
          <DialogActions className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={handleDialogClose}>
              Cancel
            </Button>
            <div
              style={{
                width: '100%',
                padding: '10px 15px',
                background: 'rgba(255,100,0,.1)',
                borderRadius: '4px',
                border: '1px solid rgba(255,100,0,.3)',
                // display: 'flex',
                display: 'none',
                justifyContent: 'space-between',
                alignItems: 'center',
                maxWidth: '400px',
                position: 'relative',
              }}
            >
              <span>Work orders already exists for the selected services. Do you still want to create new?</span>
              <div>
                <Button className="btn btn-sm btn-primary">OK</Button>
                <Link
                  className="  "
                  to={''}
                  style={{
                    margin: '0',
                    padding: '4px 5px 1px',
                    position: 'absolute',
                    right: '-15px',
                    top: '-10px',
                    background: '#fff',
                    borderRadius: '30px',
                  }}
                >
                  {/* <img src={Icons.IconClose} alt="Close" /> */}
                </Link>
              </div>
            </div>
            <Button
              type={'button'}
              variant="contained"
              disableElevation={true}
              style={styling.buttonStyle}
              disabled={isSaving || isReadOnlyPermission}
              onClick={handleSaveClick}
            >
              Save
            </Button>
          </DialogActions>
        </div>
      </Dialog>
      {isConfirmation && (
        <ConfirmDialog
          message="Work order already exists for the selected service. Do you still want to create new?"
          onClose={handleClose}
          onConfirm={handleConfirm}
          open={isConfirmation}
          isLoading={isSaving}
        />
      )}
    </>
  );
};
